import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Frontend cache for address searches (5 minute TTL)
const addressCache = new Map();
const ADDRESS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCachedAddress = (query) => {
  const key = query.toLowerCase().trim();
  const cached = addressCache.get(key);
  if (cached && Date.now() - cached.timestamp < ADDRESS_CACHE_TTL) {
    return cached.data;
  }
  return null;
};

const setCachedAddress = (query, data) => {
  const key = query.toLowerCase().trim();
  addressCache.set(key, { data, timestamp: Date.now() });
  
  // Clean up old entries (keep cache size reasonable)
  if (addressCache.size > 100) {
    const entries = Array.from(addressCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    for (let i = 0; i < 50; i++) {
      addressCache.delete(entries[i][0]);
    }
  }
};

export const api = {
  getVehicles: async () => {
    const response = await axios.get(`${API}/vehicles`);
    return response.data;
  },
  
  createBooking: async (bookingData) => {
    const response = await axios.post(`${API}/bookings`, bookingData);
    return response.data;
  },
  
  getBooking: async (bookingId) => {
    const response = await axios.get(`${API}/bookings/${bookingId}`);
    return response.data;
  },
  
  lookupBooking: async (bookingId, email) => {
    const response = await axios.post(`${API}/bookings/lookup`, { booking_id: bookingId, email });
    return response.data;
  },
  
  createCheckout: async (bookingId, origin) => {
    const response = await axios.post(`${API}/payments/checkout`, { booking_id: bookingId, origin });
    return response.data;
  },
  
  getPaymentStatus: async (sessionId) => {
    const response = await axios.get(`${API}/payments/status/${sessionId}`);
    return response.data;
  },
  
  calculatePrice: async (data) => {
    const response = await axios.post(`${API}/calculate-price`, data);
    return response.data;
  },
  
  calculateDistance: async (pickup, dropoff, stopovers = 0) => {
    const response = await axios.post(`${API}/calculate-distance`, { pickup, dropoff, stopovers });
    return response.data;
  },
  
  // OneMap API - Search any Singapore address (with caching)
  searchAddress: async (query) => {
    // Check frontend cache first
    const cached = getCachedAddress(query);
    if (cached) {
      return cached;
    }
    
    const response = await axios.get(`${API}/onemap/search`, { params: { query, limit: 10 } });
    const data = response.data;
    
    // Cache the result
    setCachedAddress(query, data);
    
    return data;
  },
  
  // Calculate distance using coordinates from OneMap
  calculateDistanceWithCoords: async (data) => {
    const response = await axios.post(`${API}/calculate-distance-onemap`, data);
    return response.data;
  },
  
  getLocations: async () => {
    const response = await axios.get(`${API}/locations`);
    return response.data;
  },
  
  getBlockedDates: async () => {
    const response = await axios.get(`${API}/blocked-dates`);
    return response.data;
  },
  
  getBlockedForDate: async (date) => {
    const response = await axios.get(`${API}/blocked-dates/${date}`);
    return response.data;
  },
  
  // CMS Content
  getContent: async (section = null) => {
    const url = section ? `${API}/content/${section}` : `${API}/content`;
    const response = await axios.get(url);
    return response.data;
  }
};
